
import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useSubscription } from './hooks/useSubscription';
import { useCheckoutRedirect } from './hooks/useCheckoutRedirect';
import CurrentSubscription from './components/CurrentSubscription';
import AvailablePlans from './components/AvailablePlans';
import PaymentMethods from './components/PaymentMethods';

export default function BillingPage() {
  const { toast } = useToast();
  const { 
    subscription, 
    plans, 
    loading, 
    processingPlan,
    handleSubscribe,
    handleManageSubscription 
  } = useSubscription();
  
  // Check if we just returned from a checkout session
  // We don't need to pass toast anymore as the hook now handles toasts internally
  useCheckoutRedirect();

  return (
    <DashboardLayout>
      <motion.div
        className="space-y-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex items-center justify-between">
          <motion.h1
            className="text-3xl font-semibold"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            Billing
          </motion.h1>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Current Subscription */}
            {subscription && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <CurrentSubscription 
                  subscription={subscription}
                  loading={loading}
                  onManage={handleManageSubscription}
                />
              </motion.div>
            )}

            {/* Payment Methods */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
            >
              <PaymentMethods />
            </motion.div>

            {/* Available Plans */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <AvailablePlans 
                plans={plans}
                subscription={subscription}
                processingPlan={processingPlan}
                onSubscribe={handleSubscribe}
              />
            </motion.div>
          </div>
        )}
      </motion.div>
    </DashboardLayout>
  );
}
